import { useIsMutating, useMutation } from "@tanstack/react-query";
import { useRefreshBookingData } from "PFApp/booking/hooks/use_refresh_booking_data";
import { useSyncRows } from "PFApp/booking/parts/overview/use_sync_rows";
import { useMatchesShortlistReplace } from "PFCore/hooks/queries/matches/use_matches_shortlist_replace";
import { MutationMeta } from "PFCore/query_client";
import {
  acceptShortlist,
  assignShortlist,
  bookShortlist,
  declineShortlist,
  fillAndBookShortlist,
  fillShortlist,
  rejectShortlist,
  resetShortlist,
  ShortlistBookingDates
} from "PFCore/services/shortlist";
import { Activity, Shortlist, ShortlistMinimized } from "PFTypes";
import { Id } from "PFTypes/id";
import { MutationOptions } from "PFTypes/request_options";

import { activityKeys } from "../activity/query_keys";
import { bookingEngagementsKeys } from "../bookings/engagements/query_keys";
import { profileKeys } from "../profile";
import { mutationKeys } from "./mutation_keys";
import { rankedShortlistsKeys, shortlistsKeys } from "./query_keys";

export type ShortlistDecisionParams = {
  shortlistId: Id;
  reasons?: Shortlist["reasons"];
  action: "accept" | "decline";
};

export const useShortlistDecision = (
  options: MutationOptions<ShortlistDecisionParams, ShortlistMinimized> = {},
  { disabled }: MutationMeta["invalidation"] = {}
) => {
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();

  return useMutation({
    mutationKey: mutationKeys.makeDecision(),
    mutationFn: async ({
      shortlistId,
      reasons,
      action
    }: ShortlistDecisionParams): Promise<ShortlistMinimized> => {
      const queryFunction = action === "accept" ? acceptShortlist : declineShortlist;
      return queryFunction(shortlistId, { reasons });
    },
    ...options,
    meta: {
      invalidation: {
        disabled,
        replace: true,
        composeQueryKeys: (shortlist: ShortlistMinimized) => [
          shortlistsKeys.activityShortlists(shortlist.activityId),
          rankedShortlistsKeys.activityRankedShortlists(shortlist.activityId)
        ]
      }
    },
    onSuccess: (shortlist, ...args) => {
      replaceMatchesShortlistInCache(shortlist);

      options?.onSuccess?.(shortlist, ...args);
    }
  });
};

export const useShortlistAssign = (options: MutationOptions<Id, ShortlistMinimized> = {}) => {
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();

  return useMutation({
    mutationKey: mutationKeys.assign(),
    mutationFn: async (id: Id): Promise<ShortlistMinimized> => assignShortlist(id),
    ...options,
    meta: {
      invalidation: {
        replace: true,
        composeQueryKeys: (shortlist: ShortlistMinimized) => [
          shortlistsKeys.activityShortlists(shortlist.activityId),
          rankedShortlistsKeys.activityRankedShortlists(shortlist.activityId),
          activityKeys.activity(shortlist.activityId),
          bookingEngagementsKeys.economicsAll()
        ]
      }
    },
    onSuccess: (shortlist, ...args) => {
      replaceMatchesShortlistInCache(shortlist);
      options?.onSuccess?.(shortlist, ...args);
    }
  });
};

export const useShortlistFill = (
  options: MutationOptions<{ id: Id; vacancyId?: Id }, ShortlistMinimized> = {}
) => {
  const mutationKey = mutationKeys.fill();
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();

  const fillMutation = useMutation({
    mutationKey,
    mutationFn: async ({ id, vacancyId }): Promise<ShortlistMinimized> => fillShortlist(id, { vacancyId }),
    ...options,
    meta: {
      invalidation: {
        replace: true,
        composeQueryKeys: (shortlist: ShortlistMinimized) => [
          activityKeys.activity(shortlist.activityId),
          shortlistsKeys.activityShortlists(shortlist.activityId),
          shortlistsKeys.activitySuspendedShortlists(shortlist.activityId),
          rankedShortlistsKeys.activityRankedShortlists(shortlist.activityId),
          activityKeys.vacancies(shortlist.activityId)
        ]
      }
    },
    onSuccess: (shortlist, ...args) => {
      replaceMatchesShortlistInCache(shortlist);
      options?.onSuccess?.(shortlist, ...args);
    }
  });

  const isUpdating = useIsMutating({
    mutationKey
  });

  return { ...fillMutation, isUpdating };
};

type FillAndBookParams = {
  shortlistId: Id;
  vacancyId?: number;
  profileId: number;
  activity: Pick<Activity, "id" | "parent_activity_id">;
  skipBookingsCreation?: boolean;
  bookings?: ShortlistBookingDates[];
};

export const useShortlistFillAndBook = (
  options: MutationOptions<FillAndBookParams, ShortlistMinimized> = {}
) => {
  const mutationKey = mutationKeys.fillAndBook();
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();
  const syncRows = useSyncRows();
  const refreshBookingData = useRefreshBookingData();

  const fillAndBookMutation = useMutation({
    mutationFn: ({
      shortlistId,
      vacancyId,
      skipBookingsCreation,
      bookings
    }: FillAndBookParams): Promise<ShortlistMinimized> =>
      fillAndBookShortlist(shortlistId, { vacancyId, skipBookingsCreation, bookings }),
    ...options,
    meta: {
      invalidation: {
        replace: true,
        composeQueryKeys: (shortlist: ShortlistMinimized) => [
          shortlistsKeys.activityShortlists(shortlist.activityId),
          rankedShortlistsKeys.activityRankedShortlists(shortlist.activityId),
          activityKeys.vacancies(shortlist.activityId),
          profileKeys.all()
        ]
      }
    },
    onSuccess: (...args) => {
      const shortlist = args[0];
      const { profileId, activity } = args[1];
      const parentActivityIds = activity?.parent_activity_id ? [activity.parent_activity_id] : [];
      syncRows({
        profileId,
        activityId: activity?.id,
        parentActivityIds
      });
      refreshBookingData({
        profileIds: [profileId],
        activityId: activity?.id,
        shortlistId: shortlist.id,
        refreshProfile: true
      });

      replaceMatchesShortlistInCache(shortlist);
      options?.onSuccess?.(...args);
    },
    mutationKey
  });

  const isUpdating = useIsMutating({
    mutationKey
  });

  return { ...fillAndBookMutation, isUpdating };
};

type ShortlistBookPayload = {
  id: Id;
  skipBookingsCreation?: boolean;
  bookings?: ShortlistBookingDates[];
};

export const useShortlistBook = (options: MutationOptions<ShortlistBookPayload, ShortlistMinimized> = {}) => {
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();

  return useMutation({
    mutationKey: mutationKeys.book(),
    mutationFn: ({ id, skipBookingsCreation, bookings }: ShortlistBookPayload) =>
      bookShortlist(id, skipBookingsCreation, bookings),
    ...options,
    meta: {
      invalidation: {
        replace: true,
        composeQueryKeys: (shortlist: ShortlistMinimized) => [
          shortlistsKeys.activityShortlists(shortlist.activityId),
          rankedShortlistsKeys.activityRankedShortlists(shortlist.activityId)
        ]
      }
    },
    onSuccess: (shortlist, ...args) => {
      replaceMatchesShortlistInCache(shortlist);
      options?.onSuccess?.(shortlist, ...args);
    }
  });
};

export const useShortlistReset = (
  options: MutationOptions<Id, ShortlistMinimized> = {},
  { disabled }: MutationMeta["invalidation"] = {}
) => {
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();

  return useMutation({
    mutationKey: mutationKeys.reset(),
    mutationFn: async (id: Id): Promise<ShortlistMinimized> => resetShortlist(id),
    ...options,
    meta: {
      invalidation: {
        disabled,
        replace: true,
        composeQueryKeys: (shortlist: ShortlistMinimized) => [
          shortlistsKeys.activityShortlists(shortlist.activityId),
          rankedShortlistsKeys.activityRankedShortlists(shortlist.activityId),
          activityKeys.vacancies(shortlist.activityId)
        ]
      }
    },
    onSuccess: (shortlist, ...args) => {
      replaceMatchesShortlistInCache(shortlist);

      options?.onSuccess?.(shortlist, ...args);
    }
  });
};

export const useShortlistReject = (options: MutationOptions<Id, ShortlistMinimized> = {}) => {
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();

  return useMutation({
    mutationKey: mutationKeys.reject(),
    mutationFn: async (id: Id): Promise<ShortlistMinimized> => rejectShortlist(id),
    ...options,
    meta: {
      invalidation: {
        replace: true,
        composeQueryKeys: (shortlist: ShortlistMinimized) => [
          shortlistsKeys.activityShortlists(shortlist.activityId),
          rankedShortlistsKeys.activityRankedShortlists(shortlist.activityId),
          activityKeys.vacancies(shortlist.activityId),
          activityKeys.activity(shortlist.activityId),
          bookingEngagementsKeys.economicsAll()
        ]
      }
    },
    onSuccess: (shortlist, ...args) => {
      replaceMatchesShortlistInCache(shortlist);

      options?.onSuccess?.(shortlist, ...args);
    }
  });
};
